import React from "react";
import { Form } from "react-bootstrap";
import { useArsContext } from "../contexts";
import { SET_SIDEBAR_VIEW } from "../contexts/interface";

/**
 * Configuration for ArsSidebarViewRadio.
 */
export interface ArsSidebarViewRadioProps {
  showGroupingFactors?: boolean;
  showOperations?: boolean;
  showVariables?: boolean;
  /** Retained for existing test mocks; patch controls are hosted in a separate window. */
  showVariants?: boolean;
}

/**
 * ArsSidebarViewRadio provides radio button controls to switch between the
 * enabled table sidebar views.
 *
 * @param props - Component props
 * @param props.showGroupingFactors - Whether the grouping-factor view is available
 * @param props.showOperations - Whether the operations view is available
 * @param props.showVariables - Whether the variables view is available
 * @returns Radio button group element
 */
export const ArsSidebarViewRadio = ({
  showGroupingFactors = true,
  showOperations = false,
  showVariables = false,
}: ArsSidebarViewRadioProps): React.ReactNode => {
  const { state, dispatch } = useArsContext();

  const currentView = state.currentSidebar.view;

  return (
    <div className="d-flex gap-3">
      {showVariables && (
        <Form.Check
          type="radio"
          id="ars-table-sidebar-view-variables"
          label="Variables"
          name="ars-table-sidebar-view"
          value="variable"
          checked={currentView === "variable"}
          onChange={() => {
            dispatch({
              operation: SET_SIDEBAR_VIEW,
              view: "variable",
            });
          }}
        />
      )}
      {showGroupingFactors && (
        <Form.Check
          type="radio"
          id="ars-table-sidebar-view-grouping-factors"
          label="Grouping factors"
          name="ars-table-sidebar-view"
          value="groupingFactor"
          checked={currentView === "groupingFactor"}
          onChange={() => {
            dispatch({
              operation: SET_SIDEBAR_VIEW,
              view: "groupingFactor",
            });
          }}
        />
      )}
      {showOperations && (
        <Form.Check
          type="radio"
          id="ars-table-sidebar-view-operations"
          label="Operations"
          name="ars-table-sidebar-view"
          value="operation"
          checked={currentView === "operation"}
          onChange={() => {
            dispatch({
              operation: SET_SIDEBAR_VIEW,
              view: "operation",
            });
          }}
        />
      )}
    </div>
  );
};

ArsSidebarViewRadio.displayName = "ArsSidebarViewRadio";
